import { FilterBaseInterface, FilterPresetEnum, InboxFilter, PossibleMatchesFilter, SubscriptionFilter, TransactionFilter, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Store } from "antd/lib/form/interface"
import { deepEqual } from "fast-equals"
import React from "react"

import type { ExpenseFilter } from "Components/forms/expenseFilter.form"
import type { WorkflowFilter } from "Components/forms/workflowFilter.form"
import store from "Shared/store"
import { setTableFilter, updateTableFilter } from "Shared/store/actions/tables/tableActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { formatRemovalFilterOptions, getCurrentPresets } from "Shared/utils/filter.utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

/**
 * Apply newest filter object to the filter of a table. If shouldSetBackendFilter is not set or set to true, changes will be sent to the API to store the latest filter state
 *
 * @param table Table that is affected by the filter
 * @param updatedFilterObject The new filter object
 * @param ignorePresets If triggered from quickFilter
 * @param shouldSetBackendFilter Should the webapp sync the filter state with the backend? Should be false for socket handling (as we already receive the latest state from the API)
 */
export const applyFilter = async (table: TablesEnum, updatedFilterObject: object, ignorePresets = false, shouldSetBackendFilter = true) => {
    // only updated when needed
    const currentFilterObject = store.getState().tables[table].data.filter
    const currentPresets = getCurrentPresets(currentFilterObject)

    if (!ignorePresets) {
        updatedFilterObject = {
            ...updatedFilterObject,
            presets: currentPresets,
        }
    }

    const isDuplicate = deepEqual(currentFilterObject, updatedFilterObject)

    if (!isDuplicate) {
        if (shouldSetBackendFilter) {
            store.dispatch(setTableFilter(table, updatedFilterObject))
        } else {
            store.dispatch(updateTableFilter(table, updatedFilterObject))
        }
    }
}

export const setQuickFilter = (table: TablesEnum, presets: Array<FilterPresetEnum>) => {
    const filterObject = store.getState().tables[table].data.filter
    applyFilter(table, { ...filterObject, presets }, true)
}

export const resetFilter = (table: TablesEnum, except = {}) => {
    let shouldFetch = true
    if ([TablesEnum.TRANSACTIONS_ARCHIVE, TablesEnum.ARCHIVE].includes(table)) shouldFetch = false

    store.dispatch(setTableFilter(table, { ...except }, shouldFetch))
}

export const removeFilterOption = (table: TablesEnum, key: string, filterObject: FilterBaseInterface) => {
    const { formattedFilterObject, shouldFetch } = formatRemovalFilterOptions(table, key, filterObject)

    store.dispatch(setTableFilter(table, formattedFilterObject, shouldFetch))
}

type CommonFilterType = ExpenseFilter | TransactionFilter | SubscriptionFilter | PossibleMatchesFilter | WorkflowFilter | InboxFilter

export const useFilterFormCreditorChange = (
    filterState: CommonFilterType,
    setFilterState: React.Dispatch<React.SetStateAction<CommonFilterType>>,
    onSubmit: (values: Store) => void,
) => {
    const [creditorDropdownType, setCreditorDropdownType] = useStateIfMounted(filterState?.creditorUser ? VendorTypeEnum.USER : VendorTypeEnum.VENDOR)

    const handleFinish = (values: Store) => {
        const formValues = {
            ...values,
            ...(creditorDropdownType === VendorTypeEnum.USER ? { creditorUser: filterState.creditorUser, vendor: null } : { vendor: filterState.vendor }),
        }

        onSubmit(formValues)
    }

    const onCreditorChange = (creditorId: string) => {
        setFilterState((currentFilterState: CommonFilterType) => ({
            ...currentFilterState,
            ...(creditorDropdownType === VendorTypeEnum.USER ? { creditorUser: creditorId } : { vendor: creditorId }),
        }))
    }

    return [{ creditorDropdownType }, { handleFinish, setCreditorDropdownType, onCreditorChange }] as const
}
